/**
 * «Блюда и товары» — сопоставление блюд кассы с товарами склада (подэтап
 * M2 финансового агента). Без связи у блюда нет себестоимости.
 *
 * Система связывает сама только однозначное. Здесь — то, что ждёт человека:
 * «Нужен выбор» (на складе несколько одинаково названных товаров — система
 * не выбирает за вас) и «Не нашлось на складе» (товар ищется по названию
 * или артикулу). «Это он» подписывает связь вашим именем, «не он» больше не
 * предлагается. Порядок — по выручке за 30 дней: сначала то, что продаётся.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { qty, rub } from '../../api';
import { Notice, when } from '../../ui';
import { stockApi } from './api';
import type { Goods, Matching, MatchingAnswer, Sold } from './api';

const SHOW = 40;

export function Dishes() {
  const client = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const query = useQuery<Matching, ApiError>({
    queryKey: ['stock', 'matching'],
    queryFn: () => stockApi.matching(),
    refetchOnWindowFocus: true,
  });
  const decide = useMutation<
    MatchingAnswer,
    ApiError,
    { itemId: number; goodsId: string; confirm: boolean }
  >({
    mutationFn: ({ itemId, goodsId, confirm }) =>
      confirm ? stockApi.confirm(itemId, goodsId) : stockApi.reject(itemId, goodsId),
    onSuccess: (answer) => {
      client.setQueryData(['stock', 'matching'], answer.matching);
      setResult({ ok: true, text: answer.message });
      setOpen(null);
    },
    onError: (error) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const data = query.data;
  const act = (itemId: number, goodsId: string, confirm: boolean) => {
    setResult(null);
    decide.mutate({ itemId, goodsId, confirm });
  };

  return (
    <div className="stock-stack">
      <p className="stock-hint">
        Себестоимость блюда берётся у товара склада, с которым оно связано. Однозначное система
        связывает сама; здесь — то, что ждёт вашего решения. Порядок — по выручке за {data.days}{' '}
        дней.
      </p>
      <div className="stock-tiles">
        <div className="stock-tile">
          <span className="stock-tile__label">Связано</span>
          <span className="stock-tile__value">
            {data.counts.matched} из {data.counts.dishes}
          </span>
        </div>
        <div className={`stock-tile${data.counts.ambiguous ? ' stock-tile--warn' : ''}`}>
          <span className="stock-tile__label">Нужен выбор</span>
          <span className="stock-tile__value">{data.counts.ambiguous}</span>
        </div>
        <div className="stock-tile">
          <span className="stock-tile__label">Не нашлось на складе</span>
          <span className="stock-tile__value">{data.counts.unmatched}</span>
        </div>
      </div>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}

      <section className="stock-card" aria-label="Нужен выбор">
        <header className="stock-card__head">
          <h2 className="stock-card__title">Нужен выбор</h2>
          <span className="faint">{data.ambiguous.length}</span>
        </header>
        {data.ambiguous.length === 0 ? (
          <p className="faint">Все неоднозначные блюда разобраны.</p>
        ) : (
          <>
            <p className="faint">
              На складе несколько товаров с одинаковым названием — выбирать за вас система не
              станет: половина себестоимости оказалась бы у другого товара.
            </p>
            <ul className="stock-list">
              {data.ambiguous.slice(0, SHOW).map((dish) => (
                <li key={dish.item_id} className="stock-dish">
                  <DishHead name={dish.name} code={dish.code} sold={dish.sold} />
                  <ul className="stock-candidates">
                    {dish.candidates.map((goods) => (
                      <li key={goods.goods_id} className="stock-candidate">
                        <GoodsLabel goods={goods} />
                        <span className="stock-buttons">
                          <button
                            type="button"
                            className="button button--small button--primary"
                            disabled={decide.isPending}
                            onClick={() => act(dish.item_id, goods.goods_id, true)}
                          >
                            Это он
                          </button>
                          <button
                            type="button"
                            className="button button--small"
                            disabled={decide.isPending}
                            onClick={() => act(dish.item_id, goods.goods_id, false)}
                          >
                            Не он
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="stock-card" aria-label="Не нашлось на складе">
        <header className="stock-card__head">
          <h2 className="stock-card__title">Не нашлось на складе</h2>
          <span className="faint">{data.unmatched.length}</span>
        </header>
        {data.unmatched.length === 0 ? (
          <Empty title="Все блюда связаны" />
        ) : (
          <>
            <p className="faint">
              У этих блюд не будет себестоимости, пока их не связать. Найдите товар по названию
              или артикулу.
            </p>
            <ul className="stock-list">
              {data.unmatched.slice(0, SHOW).map((dish) => (
                <li key={dish.item_id} className="stock-dish">
                  <div className="stock-dish__line">
                    <DishHead name={dish.name} code={dish.code} sold={dish.sold} />
                    {open !== dish.item_id && (
                      <button type="button" className="button button--small" onClick={() => setOpen(dish.item_id)}>
                        Найти товар
                      </button>
                    )}
                  </div>
                  {open === dish.item_id && (
                    <GoodsPicker
                      busy={decide.isPending}
                      onPick={(goods) => act(dish.item_id, goods.goods_id, true)}
                      onClose={() => setOpen(null)}
                    />
                  )}
                </li>
              ))}
            </ul>
            {data.unmatched.length > SHOW && (
              <p className="faint">…и ещё {data.unmatched.length - SHOW} — продаются меньше.</p>
            )}
          </>
        )}
      </section>

      {data.decisions.length > 0 && (
        <section className="stock-card" aria-label="Решения">
          <h2 className="stock-card__title">Последние решения</h2>
          <ul className="stock-list">
            {data.decisions.map((row) => (
              <li key={`${row.item_id}:${row.goods_id}:${row.status}`} className="stock-row">
                <span className="stock-row__title">
                  {row.item_name} {row.status === 'confirmed' ? '→' : '≠'} {row.goods_name ?? row.goods_id}
                </span>
                <span className="faint">
                  {row.status === 'confirmed' ? 'связано' : 'не предлагать'}
                  {row.decided_by ? ` · ${row.decided_by}` : ''}
                  {row.decided_at ? ` · ${when(row.decided_at)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function DishHead({ name, code, sold }: { name: string; code: string | null; sold: Sold }) {
  return (
    <span className="stock-row__main">
      <span className="stock-row__title">{name}</span>
      <span className="faint">
        {code ? `код ${code} · ` : ''}
        {sold.quantity > 0 ? `продано ${qty(String(sold.quantity))} на ${rub(sold.amount)}` : 'не продавалось'}
      </span>
    </span>
  );
}

function GoodsLabel({ goods }: { goods: Goods }) {
  return (
    <span className="stock-row__main">
      <span>{goods.name}</span>
      <span className="faint">
        {[goods.code ? `артикул ${goods.code}` : null, goods.unit].filter(Boolean).join(' · ') ||
          'без артикула'}
      </span>
    </span>
  );
}

function GoodsPicker({
  busy,
  onPick,
  onClose,
}: {
  busy: boolean;
  onPick: (goods: Goods) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const search = useQuery<Goods[], ApiError>({
    queryKey: ['stock', 'goods', q.trim()],
    queryFn: () => stockApi.goods(q.trim()),
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="stock-picker">
      <div className="stock-dish__line">
        <input
          className="input stock-picker__input"
          aria-label="Товар склада"
          placeholder="Название или артикул товара"
          value={q}
          autoFocus
          onChange={(event) => setQ(event.target.value)}
        />
        <button type="button" className="button button--small" onClick={onClose}>
          Закрыть
        </button>
      </div>
      {q.trim().length >= 2 &&
        (search.isPending ? (
          <p className="faint">Ищу…</p>
        ) : search.error ? (
          <p className="faint">Поиск не удался.</p>
        ) : search.data.length === 0 ? (
          <p className="faint">Ничего не нашлось.</p>
        ) : (
          <ul className="stock-candidates">
            {search.data.map((goods) => (
              <li key={goods.goods_id} className="stock-candidate">
                <GoodsLabel goods={goods} />
                <button
                  type="button"
                  className="button button--small button--primary"
                  disabled={busy}
                  onClick={() => onPick(goods)}
                >
                  Связать
                </button>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}
